<template>
  <div class="header-wrapper" ref="headerWrapper">
    <header ref="headerRef" id="header">
      <div class="logo-wrapper">
        <router-link to="/" @click="closeMenu">
          <Logotype />
        </router-link>
      </div>

      <!-- Desktop nav -->
      <nav>
        <div class="links-wrapper">
          <router-link class="uppercase" to="/">{{ $t('nav.home') }}</router-link>
          <router-link class="uppercase" to="/about">{{ $t('nav.about') }}</router-link>
          <router-link class="uppercase" to="/services">{{ $t('nav.services') }}</router-link>
          <router-link class="uppercase" to="/cases">{{ $t('nav.cases') }}</router-link>
          <router-link class="uppercase" to="/contacts">{{ $t('nav.contacts') }}</router-link>
          <!-- <LocaleSwitcher /> -->
        </div>
      </nav>

      <!-- Burger button -->
      <img
        id="icon-menu"
        :class="{ hidden: isMenuOpen }"
        src="/images/icons/menu.svg"
        alt="Open menu"
        @click="openMenu"
      />

      <!-- Close button -->
      <img
        id="icon-menu-cross"
        :class="{ visible: isMenuOpen }"
        src="/images/icons/cross.svg"
        alt="Close menu"
        @click="closeMenu"
      />
    </header>

    <!-- Mobile menu -->
    <nav class="mobile-menu" :class="{ active: isMenuOpen }" ref="menuLinks">
      <router-link class="uppercase" to="/">{{ $t('nav.home') }}</router-link>
      <router-link class="uppercase" to="/about">{{ $t('nav.about') }}</router-link>
      <router-link class="uppercase" to="/services">{{ $t('nav.services') }}</router-link>
      <router-link class="uppercase" to="/cases">{{ $t('nav.cases') }}</router-link>
      <router-link class="uppercase" to="/contacts">{{ $t('nav.contacts') }}</router-link>
      <!-- <LocaleSwitcher class="mobile-lang" /> -->
    </nav>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import LocaleSwitcher from '@ozdao/martyrs/src/modules/core/views/components/partials/LocaleSwitcher.vue'
import Logotype from '@/components/icons/Logotype.vue'
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger.js"

const router = useRouter()

gsap.registerPlugin(ScrollTrigger)

const headerRef = ref(null)
const headerWrapper = ref(null)
const menuLinks = ref(null)
const isMenuOpen = ref(false)

function checkMaxWidth (width) {
  return window.matchMedia('(max-width: ' + width + ')').matches
}

let menuTimeline = null

function openMenu() {
  if (isMenuOpen.value) return
  isMenuOpen.value = true
  document.getElementById('screen').style.overflow = 'hidden'

  const wrapper = headerWrapper.value
  const links = menuLinks.value.querySelectorAll('a')

  if (menuTimeline) menuTimeline.kill()

  menuTimeline = gsap.timeline()

  // Подложка меню — фон страницы. GSAP не умеет твинить var(), поэтому значение
  // токена читается на месте: иначе в светлой теме меню открывалось бы чёрным.
  const bg_canvas = getComputedStyle(document.documentElement).getPropertyValue('--bg-canvas').trim()

  // Background fade in
  menuTimeline.fromTo(wrapper,
    { backgroundColor: `rgba(${bg_canvas}, 0)` },
    { backgroundColor: `rgba(${bg_canvas}, 1)`, duration: 0.4, ease: 'power2.out' }
  )

  // Links stagger animation
  menuTimeline.fromTo(links,
    { autoAlpha: 0, y: 60, skewY: 3 },
    {
      autoAlpha: 1,
      y: 0,
      skewY: 0,
      duration: 0.5,
      stagger: 0.08,
      ease: 'back.out(1.2)'
    },
    '-=0.2'
  )
}

function closeMenu() {
  if (!isMenuOpen.value) return

  const wrapper = headerWrapper.value
  const links = menuLinks.value.querySelectorAll('a')

  if (menuTimeline) menuTimeline.kill()

  menuTimeline = gsap.timeline({
    onComplete: () => {
      isMenuOpen.value = false
      const screenEl = document.getElementById('screen')
      if (screenEl) screenEl.style.overflow = ''
    }
  })

  // Links reverse stagger
  menuTimeline.to(links, {
    autoAlpha: 0,
    y: -30,
    duration: 0.25,
    stagger: { each: 0.04, from: 'end' },
    ease: 'power2.in'
  })

  // Background fade out
  menuTimeline.to(wrapper, {
    backgroundColor: `rgba(${getComputedStyle(document.documentElement).getPropertyValue('--bg-canvas').trim()}, 0)`,
    duration: 0.3,
    ease: 'power2.in'
  }, '-=0.1')
}

onMounted(() => {
  ScrollTrigger.create({
    start: 'top -150',
    end: 9999,
    onEnter: () => {
      var first = headerRef.value.getBoundingClientRect()
      headerWrapper.value.classList.add('sticky')
      var last = headerRef.value.getBoundingClientRect()
      var invert = first.top - last.top
      gsap.fromTo('#header', {
        y: invert,
        force3D: false,
        padding: checkMaxWidth('640px') ? "2rem" : '2.5rem 2rem',
      }, {
        y: 0,
        padding: checkMaxWidth('640px') ? "1rem" : '1.25rem 2rem',
        ease: "cubic-bezier(.08,.75,.77,1.03)",
      })
    },
    onLeaveBack: () => {
      var first = headerRef.value.getBoundingClientRect()
      headerWrapper.value.classList.remove('sticky')
      var last = headerRef.value.getBoundingClientRect()
      var invert = first.top - last.top
      gsap.fromTo('#header', {
        y: invert,
        force3D: false,
        padding: checkMaxWidth('640px') ? "1rem" : '1.25rem 2rem',
      }, {
        y: 0,
        padding: checkMaxWidth('640px') ? "2rem" : '2.5rem 2rem',
        ease: "cubic-bezier(.08,.75,.77,1.03)",
      })
    }
  })

  gsap.to('.header-wrapper', {
    autoAlpha: 1,
    y: 0,
    duration: 1,
  })

  // Hide/show on scroll direction
  let lastScrollY = 0
  let ticking = false
  let isHidden = false
  const scrollview = document.querySelector('#screen')

  const updateHeader = () => {
    if (!headerWrapper.value || !scrollview) return
    const scrollY = scrollview.scrollTop

    if (scrollY > lastScrollY && scrollY > 200 && !isHidden) {
      isHidden = true
      gsap.to(headerWrapper.value, { yPercent: -100, duration: 0.33, ease: "cubic-bezier(.08,.75,.77,1.03)" })
    } else if (scrollY < lastScrollY && isHidden) {
      isHidden = false
      gsap.to(headerWrapper.value, { yPercent: 0, duration: 0.33, ease: "cubic-bezier(.08,.75,.77,1.03)" })
    }

    lastScrollY = scrollY
    ticking = false
  }

  const handleScroll = () => {
    if (!ticking) {
      requestAnimationFrame(updateHeader)
      ticking = true
    }
  }

  if (scrollview) {
    scrollview.addEventListener('scroll', handleScroll, { passive: true })
  }
})

// Закрываем меню после навигации (не блокируя её)
const unregisterAfterEach = router.afterEach(() => {
  if (isMenuOpen.value) {
    closeMenu()
  }
})

onUnmounted(() => {
  unregisterAfterEach()
})
</script>
